import React from 'react';
import type { ApprovalDocumentPreviewProps } from './approvalDocument.types';

const formatDateTime = (dateString: string): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatValue = (value: number | undefined): string => {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export const ApprovalDocumentPreview: React.FC<ApprovalDocumentPreviewProps> = ({ data, showPageBreaks = false }) => {
  const { metadata, request, finalApproval, workflowSteps, attachments, consolidation, consolidationRef, disclaimer } = data;

  // Styles for A4 document
  const styles = {
    // A4 Page container - now a table for repeating header/footer
    page: {
      width: '210mm',
      minHeight: '297mm',
      backgroundColor: '#ffffff',
      fontFamily: 'Arial, Helvetica, sans-serif',
      fontSize: '10pt',
      lineHeight: 1.4,
      color: '#1a1a1a',
      boxSizing: 'border-box' as const,
      borderCollapse: 'collapse' as const,
    },
    // Table cell padding for header/body/footer
    pageCell: {
      padding: '0 20mm',
      verticalAlign: 'top' as const,
    },
    pageHeaderCell: {
      padding: '15mm 20mm 0 20mm',
      verticalAlign: 'top' as const,
    },
    pageFooterCell: {
      padding: '0 20mm 15mm 20mm',
      verticalAlign: 'bottom' as const,
    },

    // Header section
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      borderBottom: '3px solid #1e40af',
      paddingBottom: '12px',
      marginBottom: '20px',
    },
    headerLeft: {
      display: 'flex',
      flexDirection: 'row' as const,
      alignItems: 'center',
      gap: '12px',
    },
    headerLogo: {
      width: '48px',
      height: '48px',
      objectFit: 'contain' as const,
    },
    headerBrandingText: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '2px',
    },
    companyName: {
      fontSize: '14pt',
      fontWeight: 700,
      color: '#1e40af',
      margin: 0,
    },
    systemName: {
      fontSize: '9pt',
      color: '#64748b',
      margin: 0,
    },
    headerRight: {
      textAlign: 'right' as const,
    },
    documentTitle: {
      fontSize: '16pt',
      fontWeight: 700,
      color: '#1e3a5f',
      margin: '0 0 8px 0',
    },
    documentMeta: {
      fontSize: '8pt',
      color: '#64748b',
      margin: '2px 0',
    },

    // Section styles
    section: {
      marginBottom: '16px',
    },
    sectionTitle: {
      fontSize: '11pt',
      fontWeight: 700,
      color: '#1e3a5f',
      margin: '0 0 10px 0',
      paddingBottom: '4px',
      borderBottom: '1px solid #e2e8f0',
    },

    // Table styles
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const,
      fontSize: '9pt',
    },
    tableHeader: {
      backgroundColor: '#f1f5f9',
      fontWeight: 600,
      textAlign: 'left' as const,
      padding: '8px 10px',
      borderBottom: '1px solid #cbd5e1',
      color: '#334155',
    },
    tableCell: {
      padding: '8px 10px',
      borderBottom: '1px solid #e2e8f0',
      verticalAlign: 'top' as const,
    },
    tableLabelCell: {
      padding: '8px 10px',
      borderBottom: '1px solid #e2e8f0',
      backgroundColor: '#f8fafc',
      fontWeight: 600,
      width: '35%',
      color: '#475569',
    },
    tableValueCell: {
      padding: '8px 10px',
      borderBottom: '1px solid #e2e8f0',
      color: '#1e293b',
    },

    // Description Section styles
    descriptionSection: {
      marginTop: '-10px',
      marginBottom: '6px',
      padding: '10px 12px',
      backgroundColor: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: '4px',
      pageBreakInside: 'avoid' as const,
    },
    descriptionTitle: {
      fontSize: '10pt',
      fontWeight: 600,
      color: '#475569',
      margin: '0 0 4px 0',
    },
    descriptionText: {
      fontSize: '9pt',
      color: '#1e293b',
      margin: 0,
      lineHeight: 1.5,
      whiteSpace: 'pre-wrap' as const,
    },

    // Final Approval Box (highlighted)
    finalApprovalBox: {
      border: '2px solid #16a34a',
      borderRadius: '6px',
      padding: '16px',
      backgroundColor: '#f0fdf4',
      marginBottom: '16px',
    },
    finalApprovalHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      marginBottom: '12px',
    },
    finalApprovalIcon: {
      width: '28px',
      height: '28px',
      borderRadius: '50%',
      backgroundColor: '#16a34a',
      color: '#ffffff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '16pt',
      fontWeight: 700,
    },
    finalApprovalTitle: {
      fontSize: '12pt',
      fontWeight: 700,
      color: '#15803d',
      margin: 0,
    },
    finalApprovalDecision: {
      display: 'inline-block',
      padding: '4px 12px',
      backgroundColor: '#16a34a',
      color: '#ffffff',
      fontSize: '10pt',
      fontWeight: 700,
      borderRadius: '4px',
      textTransform: 'uppercase' as const,
      marginLeft: 'auto',
    },
    finalApprovalGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '12px',
    },
    finalApprovalItem: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '2px',
    },
    finalApprovalLabel: {
      fontSize: '8pt',
      color: '#64748b',
      fontWeight: 600,
      textTransform: 'uppercase' as const,
    },
    finalApprovalValue: {
      fontSize: '10pt',
      color: '#1e293b',
      fontWeight: 500,
    },
    finalApprovalRemarks: {
      marginTop: '12px',
      paddingTop: '12px',
      borderTop: '1px solid #bbf7d0',
    },

    // Workflow table
    workflowStatusApproved: {
      color: '#16a34a',
      fontWeight: 600,
    },
    workflowStatusRejected: {
      color: '#dc2626',
      fontWeight: 600,
    },
    workflowStatusPending: {
      color: '#f59e0b',
      fontWeight: 600,
    },

    // Attachments section
    attachmentItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      padding: '8px 10px',
      backgroundColor: '#f8fafc',
      borderRadius: '4px',
      marginBottom: '6px',
      border: '1px solid #e2e8f0',
    },
    attachmentIcon: {
      fontSize: '14pt',
      marginTop: '2px',
    },
    attachmentContent: {
      flex: 1,
    },
    attachmentTitle: {
      fontSize: '9pt',
      fontWeight: 600,
      color: '#1e293b',
      margin: 0,
    },
    attachmentFileName: {
      fontSize: '8pt',
      color: '#64748b',
      margin: '2px 0 0 0',
    },
    attachmentDescription: {
      fontSize: '8pt',
      color: '#64748b',
      margin: '4px 0 0 0',
      lineHeight: 1.4,
    },

    // Consolidation section
    consolidationBox: {
      border: '1px solid #e2e8f0',
      borderRadius: '4px',
      padding: '12px',
      backgroundColor: '#f8fafc',
      marginBottom: '16px',
    },
    consolidationHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '10px',
      paddingBottom: '8px',
      borderBottom: '1px solid #e2e8f0',
    },
    consolidationTitle: {
      fontSize: '11pt',
      fontWeight: 700,
      color: '#1e3a5f',
      margin: 0,
    },
    consolidationSummary: {
      display: 'flex',
      gap: '16px',
      fontSize: '9pt',
    },
    consolidationSummaryItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    consolidationSummaryLabel: {
      color: '#64748b',
    },
    consolidationSummaryValue: {
      fontWeight: 600,
      color: '#1e293b',
    },
    consolidationTableCell: {
      padding: '6px 8px',
      borderBottom: '1px solid #e2e8f0',
      fontSize: '8pt',
      verticalAlign: 'top' as const,
    },
    consolidationTableHeader: {
      backgroundColor: '#e2e8f0',
      fontWeight: 600,
      textAlign: 'left' as const,
      padding: '6px 8px',
      fontSize: '8pt',
      color: '#334155',
    },
    consolidationTotalRow: {
      backgroundColor: '#f1f5f9',
      fontWeight: 600,
    },

    // Footer section (flows with content for multi-page support)
    footer: {
      marginTop: '24px',
      paddingTop: '16px',
      borderTop: '1px solid #e2e8f0',
    },
    footerContent: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '8px',
    },
    disclaimer: {
      fontSize: '7pt',
      color: '#94a3b8',
      lineHeight: 1.4,
      textAlign: 'center' as const,
      alignSelf: 'center',
    },

    // Digital stamp container (serrated badge style)
    stampContainer: {
      width: '80px',
      height: '80px',
      position: 'relative' as const,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      alignSelf: 'flex-end',
    },

    // Page break control classes (used as data attributes for CSS)
    avoidBreak: {
      pageBreakInside: 'avoid' as const,
    },

    // Visual page break indicator (preview only)
    pageBreakIndicator: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      margin: '16px 0',
      padding: '8px 0',
    },
    pageBreakLine: {
      flex: 1,
      height: '2px',
      background: 'repeating-linear-gradient(90deg, #3b82f6 0px, #3b82f6 8px, transparent 8px, transparent 16px)',
    },
    pageBreakLabel: {
      fontSize: '9pt',
      color: '#3b82f6',
      fontWeight: 600,
      backgroundColor: '#eff6ff',
      padding: '4px 12px',
      borderRadius: '4px',
      border: '1px solid #3b82f6',
      whiteSpace: 'nowrap' as const,
    },
  };

  // Page break indicator component (hidden during print via CSS)
  const PageBreakIndicator: React.FC<{ pageNumber: number }> = ({ pageNumber }) => {
    if (!showPageBreaks) return null;
    return (
      <div style={styles.pageBreakIndicator} className="page-break-indicator">
        <div style={styles.pageBreakLine} />
        <span style={styles.pageBreakLabel}>Page {pageNumber} ends here</span>
        <div style={styles.pageBreakLine} />
      </div>
    );
  };

  const getDecisionStyle = (decision: string) => {
    switch (decision.toLowerCase()) {
      case 'approved':
        return styles.workflowStatusApproved;
      case 'rejected':
        return styles.workflowStatusRejected;
      case 'pending':
        return styles.workflowStatusPending;
      default:
        return { color: '#64748b' };
    }
  };

  const isRejected = finalApproval.decision === 'Rejected';

  // Check if this is an individual request approved/rejected via consolidation
  const isViaConsolidation = consolidationRef?.isApprovedViaConsolidation === true;
  const consolidationNumber = consolidationRef?.consolidationRequestNumber;

  // Determine if rejection was at pre-approval stage
  // A rejection is only "pre-approval stage" if the rejecting approver is explicitly marked as a pre-approver
  const rejectedStep = workflowSteps.find(step => step.decision === 'Rejected');
  const isPreApprovalRejection = isRejected && rejectedStep && rejectedStep.isPreApprover === true && !isViaConsolidation;
  const preApprovalStage = rejectedStep?.stepNumber;

  // Find the final approver's step (if they took action)
  const finalApproverStep = workflowSteps.find(step => step.isFinalApprover === true && (step.decision === 'Approved' || step.decision === 'Rejected'));

  // Get the actual person who took action:
  // - For pre-approval rejection (by a pre-approver), use the rejecting pre-approver
  // - For regular rejection/approval, use the final approver if they took action, otherwise use finalApproval data
  const actionTakenBy = isPreApprovalRejection && rejectedStep
    ? { name: rejectedStep.approverName, role: rejectedStep.approverRole }
    : finalApproverStep
      ? { name: finalApproverStep.approverName, role: finalApproverStep.approverRole }
      : { name: finalApproval.approverName, role: finalApproval.approverRole };

  // Helper to clean up remarks - remove system markers and deduplicate
  const getCleanRemarks = (): string | undefined => {
    // For pre-approval rejection, use the actual comment from the rejecting pre-approver step
    if (isPreApprovalRejection && rejectedStep?.comments) {
      return rejectedStep.comments;
    }

    // For non-pre-approval cases, prefer final approver's comments if they took action
    if (finalApproverStep?.comments) {
      return finalApproverStep.comments;
    }

    // Fall back to finalApproval.remarks
    if (!finalApproval.remarks) return undefined;

    let remarks = finalApproval.remarks;

    // Remove system markers
    remarks = remarks.replace(/\[Non-Final Approver Rejection\]/g, '').trim();

    // Remove duplicate text by splitting and keeping unique parts
    const parts = remarks.split(/[.]\s*/).filter(p => p.trim());
    const uniqueParts: string[] = [];
    const seen = new Set<string>();

    for (const part of parts) {
      const normalized = part.trim().toLowerCase();
      if (!seen.has(normalized) && normalized.length > 0) {
        seen.add(normalized);
        uniqueParts.push(part.trim());
      }
    }

    return uniqueParts.length > 0 ? uniqueParts.join('. ') : undefined;
  };

  // Generate the status text based on approval type
  const getStatusText = () => {
    if (isRejected) {
      return isPreApprovalRejection
        ? 'FINAL STATUS: REJECTED (AT PRE-APPROVAL STAGE)'
        : 'FINAL STATUS: REJECTED';
    }
    return 'FINAL STATUS: APPROVED';
  };

  const approvalBoxStyle = isRejected
    ? { ...styles.finalApprovalBox, border: '2px solid #dc2626', backgroundColor: '#fef2f2' }
    : styles.finalApprovalBox;
  const approvalTitleStyle = isRejected
    ? { ...styles.finalApprovalTitle, color: '#dc2626' }
    : styles.finalApprovalTitle;

  // Dynamic stamp colors based on approval/rejection
  const stampColor = isRejected ? '#dc2626' : '#22c55e';
  const stampText = isRejected ? 'REJECTED' : 'APPROVED';

  // Stamp Component - uses embedded SVG as data URI image for reliable print on all pages
  const SerratedStamp = () => {
    const size = 80;
    const center = size / 2;
    const outerRadius = 36;
    const innerRadius = 24;
    const textRadius = 30;

    // Generate curved text path data - position each character around the circle
    const fullText = `★${stampText}★${stampText}★${stampText}`;
    const characters = fullText.split('');
    const angleStep = 360 / characters.length;

    const textElements = characters.map((char, idx) => {
      const angleDeg = -90 + idx * angleStep;
      const angleRad = (angleDeg * Math.PI) / 180;
      const x = center + textRadius * Math.cos(angleRad);
      const y = center + textRadius * Math.sin(angleRad);
      const rotation = angleDeg + 90;
      // Escape special characters for XML
      const escapedChar = char === '&' ? '&amp;' : char === '<' ? '&lt;' : char === '>' ? '&gt;' : char;
      return `<text x="${x}" y="${y}" fill="${stampColor}" font-size="6" font-weight="bold" font-family="Arial, sans-serif" text-anchor="middle" dominant-baseline="middle" opacity="0.85" transform="rotate(${rotation} ${x} ${y})">${escapedChar}</text>`;
    }).join('');

    // Center icon
    const centerIcon = isRejected
      ? `<line x1="${center - 9}" y1="${center - 9}" x2="${center + 9}" y2="${center + 9}" stroke="${stampColor}" stroke-width="5" stroke-linecap="round" opacity="0.85"/>
         <line x1="${center + 9}" y1="${center - 9}" x2="${center - 9}" y2="${center + 9}" stroke="${stampColor}" stroke-width="5" stroke-linecap="round" opacity="0.85"/>`
      : `<polyline points="${center - 9},${center} ${center - 2},${center + 7} ${center + 11},${center - 7}" fill="none" stroke="${stampColor}" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" opacity="0.85"/>`;

    // Build complete SVG as string
    const svgString = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <circle cx="${center}" cy="${center}" r="${outerRadius}" fill="none" stroke="${stampColor}" stroke-width="3" opacity="0.85"/>
      <circle cx="${center}" cy="${center}" r="${innerRadius}" fill="none" stroke="${stampColor}" stroke-width="1.5" opacity="0.85"/>
      ${textElements}
      ${centerIcon}
    </svg>`;

    // Convert to data URI
    const dataUri = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgString)))}`;

    return (
      <img
        src={dataUri}
        alt={isRejected ? 'Rejected Stamp' : 'Approved Stamp'}
        width={size}
        height={size}
        style={{ display: 'block' }}
      />
    );
  };

  return (
    <table style={styles.page} id="approval-document">
      {/* Repeating Header for every printed page */}
      <thead className="document-header">
        <tr>
          <th style={styles.pageHeaderCell}>
            <div style={styles.header}>
              <div style={styles.headerLeft}>
                {metadata.logoUrl && (
                  <img
                    src={metadata.logoUrl}
                    alt={`${metadata.companyName} Logo`}
                    style={styles.headerLogo}
                  />
                )}
                <div style={styles.headerBrandingText}>
                  <h1 style={styles.companyName}>{metadata.companyName}</h1>
                  <p style={styles.systemName}>{metadata.systemName}</p>
                </div>
              </div>
              <div style={styles.headerRight}>
                <h2 style={styles.documentTitle}>{metadata.documentTitle}</h2>
                <p style={styles.documentMeta}>Document ID: {metadata.documentId}</p>
                <p style={styles.documentMeta}>Generated: {formatDateTime(metadata.generatedOn)}</p>
              </div>
            </div>
          </th>
        </tr>
      </thead>

      {/* Repeating Footer for every printed page */}
      <tfoot className="document-footer">
        <tr>
          <td style={styles.pageFooterCell}>
            <div style={styles.footer}>
              <div style={styles.footerContent}>
                {/* Serrated badge stamp - positioned above text */}
                <div style={styles.stampContainer}>
                  <SerratedStamp />
                </div>
                <div style={styles.disclaimer}>
                  {disclaimer ||
                    'This is a system-generated document. No physical signature is required. Verified by the Digital Approval System.'}
                </div>
              </div>
            </div>
          </td>
        </tr>
      </tfoot>

      {/* Main Content Body */}
      <tbody>
        <tr>
          <td style={styles.pageCell}>
            {/* Request Details Section */}
      <div style={styles.section} className="avoid-break">
        <h3 style={styles.sectionTitle}>Request Details</h3>
        <table style={styles.table}>
          <tbody>
            <tr>
              <td style={styles.tableLabelCell}>Request ID</td>
              <td style={styles.tableValueCell}>{request.requestId}</td>
            </tr>
            <tr>
              <td style={styles.tableLabelCell}>Request Type</td>
              <td style={styles.tableValueCell}>{request.requestType}</td>
            </tr>
            <tr>
              <td style={styles.tableLabelCell}>Subject</td>
              <td style={styles.tableValueCell}>{request.subject}</td>
            </tr>
            <tr>
              <td style={styles.tableLabelCell}>Submitted By</td>
              <td style={styles.tableValueCell}>
                {request.submittedBy}
                {request.submittedByDepartment && `, ${request.submittedByDepartment}`}
              </td>
            </tr>
            <tr>
              <td style={styles.tableLabelCell}>Submitted On</td>
              <td style={styles.tableValueCell}>{formatDateTime(request.submittedOn)}</td>
            </tr>
            {request.value !== undefined && (
              <tr>
                <td style={styles.tableLabelCell}>Value</td>
                <td style={styles.tableValueCell}>{formatValue(request.value)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Request Description Section (if available) */}
      {request.description && (
        <div style={styles.descriptionSection} className="avoid-break">
          <h4 style={styles.descriptionTitle}>Description</h4>
          <p style={styles.descriptionText}>{request.description}</p>
        </div>
      )}

      {/* Final Approval Section (Highlighted) */}
      <div style={approvalBoxStyle} className="avoid-break">
        <div style={styles.finalApprovalHeader}>
          <h3 style={approvalTitleStyle}>
            {getStatusText()}
          </h3>
        </div>
        <div style={styles.finalApprovalGrid}>
          <div style={styles.finalApprovalItem}>
            <span style={styles.finalApprovalLabel}>Action Taken By</span>
            <span style={styles.finalApprovalValue}>{actionTakenBy.name}</span>
          </div>
          <div style={styles.finalApprovalItem}>
            <span style={styles.finalApprovalLabel}>Role</span>
            <span style={styles.finalApprovalValue}>{actionTakenBy.role}</span>
          </div>
          <div style={styles.finalApprovalItem}>
            <span style={styles.finalApprovalLabel}>Decision Date & Time</span>
            <span style={styles.finalApprovalValue}>{formatDateTime(finalApproval.decisionDateTime)}</span>
          </div>
        </div>
        {/* For consolidation-approved requests, show "Approved via consolidation CON-XXX" instead of actual remarks */}
        {/* For pre-approval rejection, show the rejecting approver's actual comment */}
        {(() => {
          const cleanRemarks = getCleanRemarks();
          const showRemarks = cleanRemarks || isViaConsolidation;
          if (!showRemarks) return null;

          return (
            <div style={styles.finalApprovalRemarks}>
              <span style={styles.finalApprovalLabel}>Remarks</span>
              <div style={{ ...styles.finalApprovalValue, margin: '4px 0 0 0', whiteSpace: 'pre-wrap' }}>
                {isViaConsolidation
                  ? `${isRejected ? 'Rejected' : 'Approved'} via consolidation${consolidationNumber ? ` ${consolidationNumber}` : ''}`
                  : cleanRemarks}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Approval Workflow Section */}
      <div style={styles.section} className="workflow-section">
        <h3 style={styles.sectionTitle}>Approval Workflow</h3>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.tableHeader, width: '8%' }}>#</th>
              <th style={{ ...styles.tableHeader, width: '25%' }}>Approver</th>
              <th style={{ ...styles.tableHeader, width: '15%' }}>Decision</th>
              <th style={{ ...styles.tableHeader, width: '22%' }}>Date & Time</th>
              <th style={{ ...styles.tableHeader, width: '30%' }}>Comments</th>
            </tr>
          </thead>
          <tbody>
            {workflowSteps.map((step, index) => {
              // Check if this step was skipped due to a pre-approval rejection
              // The backend adds "[Non-Final Approver Rejection]" to comments for steps that didn't actually take action
              const isSkippedDueToPreApprovalRejection =
                step.comments?.includes('[Non-Final Approver Rejection]') ||
                step.comments?.includes('Non-Final Approver Rejection');

              // Determine the actual decision to display
              const displayDecision = isSkippedDueToPreApprovalRejection ? 'Skipped' : step.decision;

              // For individual requests approved via consolidation, replace final step's comment
              const isFinalStep = index === workflowSteps.length - 1;
              const showConsolidationComment = isViaConsolidation && isFinalStep && (step.decision === 'Approved' || step.decision === 'Rejected');

              // Determine the comment to display
              let displayComment: string;
              if (isSkippedDueToPreApprovalRejection) {
                // For skipped steps, show a cleaner message
                displayComment = 'Skipped due to pre-approval rejection';
              } else if (showConsolidationComment) {
                displayComment = `${step.decision} via consolidation${consolidationNumber ? ` ${consolidationNumber}` : ''}`;
              } else {
                displayComment = step.comments || '-';
              }

              return (
                <tr key={index}>
                  <td style={styles.tableCell}>{step.stepNumber}</td>
                  <td style={styles.tableCell}>
                    <div>{step.approverName}</div>
                    <div style={{ fontSize: '8pt', color: '#64748b' }}>{step.approverRole}</div>
                  </td>
                  <td style={{ ...styles.tableCell, ...getDecisionStyle(displayDecision) }}>{displayDecision}</td>
                  <td style={styles.tableCell}>{step.decisionDateTime && !isSkippedDueToPreApprovalRejection ? formatDateTime(step.decisionDateTime) : '-'}</td>
                  <td style={{ ...styles.tableCell, whiteSpace: 'pre-wrap' }}>
                    {displayComment}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Page break indicator after workflow (if many steps) */}
      {workflowSteps.length > 4 && <PageBreakIndicator pageNumber={1} />}

      {/* Consolidated Items Section */}
      {consolidation && consolidation.isConsolidated && (
        <div style={styles.section} className="consolidated-section">
          <div style={styles.consolidationBox}>
            <div style={styles.consolidationHeader}>
              <h3 style={styles.consolidationTitle}>Consolidated Items</h3>
              <div style={styles.consolidationSummary}>
                <div style={styles.consolidationSummaryItem}>
                  <span style={styles.consolidationSummaryLabel}>Total Items:</span>
                  <span style={styles.consolidationSummaryValue}>{consolidation.totalItems}</span>
                </div>
                {consolidation.totalValue !== undefined && (
                  <div style={styles.consolidationSummaryItem}>
                    <span style={styles.consolidationSummaryLabel}>Total Value:</span>
                    <span style={styles.consolidationSummaryValue}>{formatValue(consolidation.totalValue)}</span>
                  </div>
                )}
              </div>
            </div>
            {consolidation.items.length > 0 ? (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.consolidationTableHeader, width: '5%' }}>#</th>
                    <th style={{ ...styles.consolidationTableHeader, width: '15%' }}>Request ID</th>
                    <th style={{ ...styles.consolidationTableHeader, width: '25%' }}>Subject</th>
                    <th style={{ ...styles.consolidationTableHeader, width: '10%' }}>Value</th>
                    <th style={{ ...styles.consolidationTableHeader, width: '15%' }}>Submitted By</th>
                    <th style={{ ...styles.consolidationTableHeader, width: '15%' }}>Pre-Approved By</th>
                    <th style={{ ...styles.consolidationTableHeader, width: '15%' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {consolidation.items.map((item, index) => (
                    <tr key={index}>
                      <td style={styles.consolidationTableCell}>{item.itemNumber}</td>
                      <td style={styles.consolidationTableCell}>{item.requestId}</td>
                      <td style={styles.consolidationTableCell}>{item.subject}</td>
                      <td style={styles.consolidationTableCell}>{item.value ? formatValue(item.value) : '-'}</td>
                      <td style={styles.consolidationTableCell}>{item.submittedBy}</td>
                      <td style={styles.consolidationTableCell}>{item.preApprovedBy || '-'}</td>
                      <td style={{ ...styles.consolidationTableCell, ...styles.workflowStatusApproved }}>{item.status}</td>
                    </tr>
                  ))}
                  {/* Total Row */}
                  {consolidation.totalValue !== undefined && (
                    <tr style={styles.consolidationTotalRow}>
                      <td style={styles.consolidationTableCell} colSpan={3}>
                        <strong>Total ({consolidation.totalItems} items)</strong>
                      </td>
                      <td style={styles.consolidationTableCell}>
                        <strong>{formatValue(consolidation.totalValue)}</strong>
                      </td>
                      <td style={styles.consolidationTableCell} colSpan={3}></td>
                    </tr>
                  )}
                </tbody>
              </table>
            ) : (
              <div style={{ padding: '16px', textAlign: 'center', color: '#64748b', fontSize: '9pt' }}>
                This is a consolidated request with {consolidation.totalItems} item(s).
                {consolidation.totalValue !== undefined && (
                  <span> Total value: {formatValue(consolidation.totalValue)}</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Page break indicator after consolidated items (if many items) */}
      {consolidation && consolidation.items.length > 6 && <PageBreakIndicator pageNumber={2} />}

      {/* Attachments Section */}
      {attachments && attachments.length > 0 && (
        <div style={styles.section} className="attachments-section">
          <h3 style={styles.sectionTitle}>Attachments ({attachments.length})</h3>
          {attachments.map((attachment, index) => (
            <div key={index} style={styles.attachmentItem} className="avoid-break">
              <span style={styles.attachmentIcon}>📎</span>
              <div style={styles.attachmentContent}>
                <p style={styles.attachmentTitle}>{attachment.title || attachment.fileName}</p>
                <p style={styles.attachmentFileName}>{attachment.fileName}</p>
                {attachment.description && (
                  <p style={styles.attachmentDescription}>{attachment.description}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

          </td>
        </tr>
      </tbody>
    </table>
  );
};

export default ApprovalDocumentPreview;
